import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../../utils/baseQuery";
import { components } from "../../types/api";

// Tipos generados desde el swagger del backend (yarn generate:types:local).
type Schemas = components["schemas"];
export type CostAnalysisView = Schemas["CostAnalysisView"];
export type CostAnalysisGroup = Schemas["CostAnalysisGroup"];
export type CostAnalysisLine = Schemas["CostAnalysisLine"];

type CostAnalysisResponse = Schemas["ApiResponse_CostAnalysisView_"];

/** Dimensiones por las que se agrupa y filtra. Mismos nombres que el backend. */
export type CostDimension =
  | "month"
  | "platform"
  | "technicalPlatform"
  | "campaignType"
  | "campaignFormat"
  | "objective"
  | "funnel"
  | "sector"
  | "clientType"
  | "briefingType"
  | "investmentPayment"
  | "company";

/** Valor de filtro que selecciona las filas sin dato (sin sector, etc.). */
export const NONE_VALUE = "__none__";

export interface CostAnalysisParams {
  /** YYYY-MM */
  from: string;
  /** YYYY-MM, incluido */
  to: string;
  groupBy: CostDimension[];
  filters: Partial<Record<CostDimension, string[]>>;
  includeUnmargined: boolean;
  /** Margen objetivo en %. null sin margen objetivo. */
  targetMargin: number | null;
}

/** Query string con los filtros repetidos (platform=A&platform=B), como los espera el backend. */
export const toCostAnalysisQuery = (params: CostAnalysisParams): string => {
  const search = new URLSearchParams();
  search.set("from", params.from);
  search.set("to", params.to);
  params.groupBy.forEach((d) => search.append("groupBy", d));
  // Sin agrupar se manda vacío: si no llega, el backend usa su agrupación por defecto.
  if (params.groupBy.length === 0) search.append("groupBy", "");
  (Object.keys(params.filters) as CostDimension[]).forEach((d) =>
    params.filters[d]?.forEach((v) => search.append(d, v))
  );
  if (params.includeUnmargined) search.set("includeUnmargined", "true");
  if (params.targetMargin !== null)
    search.set("targetMargin", String(params.targetMargin));
  return search.toString();
};

export const costAnalysisApi = createApi({
  reducerPath: "costAnalysisApi",
  baseQuery: baseQueryWithReauth,
  endpoints: (builder) => ({
    // GET /cost-analysis — coste real frente a venta, agrupado.
    getCostAnalysis: builder.query<CostAnalysisResponse, CostAnalysisParams>({
      query: (params) => `/cost-analysis?${toCostAnalysisQuery(params)}`,
    }),
  }),
});

export const { useGetCostAnalysisQuery } = costAnalysisApi;
